import React, { Component } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import validator from '@rjsf/validator-ajv8';
import { v4 as uuid } from 'uuid';

import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Box, Button, FormControl, IconButton, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

import { buttonMessages, buttonStates } from '~/components/Common/Forms/states/submitButton';

import ReservationService from '~/services/ReservationService';

import deleteNulls from '~/utils/deleteNulls';
import { ingestSchedules, prepareSchedulesList } from '~/utils/schedulesHelpers';

import Schedules from './fields/Schedules';

const DEFAULT_VALUES = {
  description: '',
  amount: '',
  currency: 'AUD',
  minimum_los: 1,
  maximum_los: 999,
  unit: '',
  is_bonus: false,
  set_schedules: false,
  schedules_list: [],
  external_id: '',
  lux_plus_tier: null,
  type: null,
  board_type: null,
  room_rate_ids: [],
};

const inclusionKeysToDeleteIfNull = ['external_id', 'category_icon'];

interface Props {
  ratePlanId: string;
  groups: Array<{
    room_rate_id: string;
    room_rate_name: string;
    room_type_id: string;
    property_id: string;
    inclusions: Array<any>;
  }>;
  schema: any;
  vendorCurrencyCode: string;
  onCancel: () => void;
  onInclusionCreated: (response: unknown) => void;
}

interface State {
  inclusionItem: any;
  saveState: string;
  errors: Array<string>;
  schema: any;
  selectedRoomRateIds: string[];
}

export default class RatePlanInclusionCreateForm extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const inclusionItem = { ...DEFAULT_VALUES, currency: props.vendorCurrencyCode };
    inclusionItem.schedules_list = ingestSchedules(inclusionItem?.schedules_list);

    const inclusionsSchema = JSON.parse(JSON.stringify(props.schema));
    delete inclusionsSchema.properties.schedules_list;
    delete inclusionsSchema.properties.room_rate_ids;

    this.state = {
      inclusionItem: deleteNulls(inclusionItem, inclusionKeysToDeleteIfNull),
      saveState: buttonStates.default,
      errors: null,
      schema: inclusionsSchema,
      selectedRoomRateIds: [],
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRoomRateIdsChange = this.handleRoomRateIdsChange.bind(this);
    this.handleScheduleChange = this.handleScheduleChange.bind(this);
    this.addNewSchedule = this.addNewSchedule.bind(this);
    this.removeSchedule = this.removeSchedule.bind(this);
  }

  handleChange = ({ formData }: IChangeEvent) => {
    this.setState({ inclusionItem: formData });
  };

  handleRoomRateIdsChange = (event: any) => {
    const value = event.target.value;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    this.setState({
      selectedRoomRateIds: selectedIds,
      inclusionItem: {
        ...this.state.inclusionItem,
        room_rate_ids: selectedIds,
      },
    });
  };

  handleScheduleChange = (value, index) => {
    const schedules = { ...this.state.inclusionItem.schedules_list };
    schedules[index] = value;

    this.setState((prevState) => ({
      inclusionItem: {
        ...prevState.inclusionItem,
        schedules_list: schedules,
      },
    }));
  };

  addNewSchedule = () => {
    const schedules = { ...this.state.inclusionItem.schedules_list };
    schedules[uuid()] = {
      activePeriod: {
        from: null,
        to: null,
      },
      travelPeriod: {
        from: null,
        to: null,
      },
      type: 'sell',
      parent: null,
    };

    this.setState((prevState) => ({
      inclusionItem: {
        ...prevState.inclusionItem,
        schedules_list: schedules,
      },
    }));
  };

  removeSchedule = (index) => {
    const schedules = { ...this.state.inclusionItem.schedules_list };
    delete schedules[index];

    this.setState((prevState) => ({
      inclusionItem: {
        ...prevState.inclusionItem,
        schedules_list: schedules,
      },
    }));
  };

  handleSubmit = ({ formData }: IChangeEvent) => {
    this.setState({
      saveState: buttonStates.saving,
      errors: null,
    });

    formData.schedules_list = prepareSchedulesList(formData?.schedules_list, null);
    formData.room_rate_ids = this.state.selectedRoomRateIds;

    return ReservationService.createRatePlanInclusion(this.props.ratePlanId, formData)
      .then((response) => {
        this.setState({
          saveState: buttonStates.saved,
        });
        this.props.onInclusionCreated(response.result);
      })
      .catch((e) => {
        const errors = e.name === 'ValidationError' ? e.errors : null;
        this.setState({
          saveState: buttonStates.failed,
          errors,
        });
      });
  };

  render() {
    const { groups, onCancel } = this.props;
    const { inclusionItem, saveState, selectedRoomRateIds } = this.state;

    return (
      <Box>
        <Form
          schema={this.state.schema}
          onSubmit={this.handleSubmit}
          formData={inclusionItem}
          onChange={this.handleChange}
          validator={validator}
        >
          {/* Room Rate IDs Dropdown */}
          <FormControl fullWidth margin="normal">
            <InputLabel>Room Rates</InputLabel>
            <Select multiple value={selectedRoomRateIds} onChange={this.handleRoomRateIdsChange} label="Room Rates">
              {groups.map((group) => (
                <MenuItem key={group.room_rate_id} value={group.room_rate_id}>
                  {group.room_rate_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box mt={2}>
            <Typography variant="h6">Schedules</Typography>

            {inclusionItem.schedules_list && (
              <Stack direction="column" spacing={2}>
                {Object.keys(inclusionItem.schedules_list).map((key, idx) => (
                  <Stack direction="row" key={key} spacing={2}>
                    <Typography>{idx + 1}.</Typography>

                    <Box flexGrow={1}>
                      <Schedules
                        key={key}
                        value={inclusionItem.schedules_list[key]}
                        onChange={(value) => this.handleScheduleChange(value, key)}
                      />
                    </Box>

                    <Box>
                      <IconButton aria-label="delete" onClick={() => this.removeSchedule(key)}>
                        <DeleteForeverIcon />
                      </IconButton>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}

            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={this.addNewSchedule} startIcon={<AddIcon />}>
                Add schedule
              </Button>
            </Box>
          </Box>

          <Stack direction="row" justifyContent="space-between" spacing={2} mt={2}>
            <Button type="button" variant="text" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saveState === buttonStates.saving}>
              {buttonMessages[saveState] || buttonStates.save}
            </Button>
          </Stack>
        </Form>
      </Box>
    );
  }
}
